import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import './ClinicalNoteWorkspace.css';
import './ClinicalNoteCollapsibleSectionCompact.css';

// Retained for compatibility with existing form navigation and future alerts.
const ACCORDION_EVENT = 'clinical-note-workspace-open-section';

/**
 * Opens a section in its group without remounting any editor.
 * Other accordion instances in the same group close themselves.
 *
 * External callers can dispatch this event with detail { group, sectionKey }.
 * Optional focus: true moves keyboard focus to the section heading after open.
 * Optional scroll: false prevents automatic scrolling (e.g. sidebar highlight).
 */
function dispatchOpenSection(group, sectionKey, options = {}) {
  if (typeof window === 'undefined' || !sectionKey) return;

  window.dispatchEvent(new CustomEvent(ACCORDION_EVENT, {
    detail: {
      group,
      sectionKey: String(sectionKey),
      scroll: options.scroll !== false,
      focus: options.focus === true,
    },
  }));
}

/**
 * Compact clinical accordion.
 *
 * Important: The editor is ALWAYS mounted; only its display is hidden.
 * This retains unsaved values, debounced autosave, the navigation coordinator,
 * and the optimistic-lock version the clinician actually reviewed.
 */
export default function ClinicalNoteCollapsibleSection({
  title,
  defaultCollapsed = true,
  resetKey,
  status,
  children,
  sectionKey,
  accordionGroup = 'clinical-note-sections',
  previousSectionKey = null,
  nextSectionKey = null,
}) {
  const [collapsed, setCollapsed] = useState(Boolean(defaultCollapsed));
  const contentId = useId();
  const containerRef = useRef(null);
  const toggleRef = useRef(null);
  const pendingFrameRef = useRef(null);
  const resolvedSectionKey = String(sectionKey || resetKey || contentId);
  const expanded = !collapsed;

  useEffect(() => {
    // A new clinical note must not inherit which panel the previous visit opened.
    setCollapsed(Boolean(defaultCollapsed));
  }, [defaultCollapsed, resetKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleAccordionOpen = (event) => {
      const detail = event?.detail || {};
      if (detail.group !== accordionGroup) return;

      if (String(detail.sectionKey) !== resolvedSectionKey) {
        // Only close panels from this visit's accordion group.
        setCollapsed(true);
        return;
      }

      setCollapsed(false);
      if (pendingFrameRef.current !== null) {
        window.cancelAnimationFrame(pendingFrameRef.current);
      }
      if (detail.scroll !== false || detail.focus === true) {
        pendingFrameRef.current = window.requestAnimationFrame(() => {
          pendingFrameRef.current = null;
          if (detail.scroll !== false) {
            containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
          if (detail.focus === true) {
            toggleRef.current?.focus({ preventScroll: true });
          }
        });
      }
    };

    window.addEventListener(ACCORDION_EVENT, handleAccordionOpen);
    return () => {
      window.removeEventListener(ACCORDION_EVENT, handleAccordionOpen);
      if (pendingFrameRef.current !== null) {
        window.cancelAnimationFrame(pendingFrameRef.current);
        pendingFrameRef.current = null;
      }
    };
  }, [accordionGroup, resolvedSectionKey]);

  const level = ['green', 'yellow', 'red', 'neutral'].includes(status?.level)
    ? status.level : 'neutral';
  const statusLabel = status?.label || 'Checking…';

  const handleToggle = useCallback(() => {
    if (expanded) {
      // Avoid leaving focus on a control about to become hidden.
      if (containerRef.current?.contains(document.activeElement) &&
          document.activeElement !== toggleRef.current) {
        toggleRef.current?.focus({ preventScroll: true });
      }
      setCollapsed(true);
    } else {
      dispatchOpenSection(accordionGroup, resolvedSectionKey, { scroll: false });
    }
  }, [expanded, accordionGroup, resolvedSectionKey]);

  const goToSection = useCallback((targetKey) => {
    if (!targetKey) return;
    // Focus the destination heading for keyboard and assistive-tech users.
    dispatchOpenSection(accordionGroup, targetKey, { focus: true, scroll: true });
  }, [accordionGroup]);

  return (
    <section
      ref={containerRef}
      className={`clinical-note-accordion clinical-note-accordion--compact clinical-note-accordion--${level} ${expanded ? 'is-expanded' : 'is-collapsed'}`}
      data-clinical-section={resolvedSectionKey}
      data-clinical-section-level={level}
      aria-label={`${title} clinical section`}
    >
      <button
        ref={toggleRef}
        type="button"
        className="clinical-note-accordion__toggle"
        onClick={handleToggle}
        aria-expanded={expanded}
        aria-controls={contentId}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${title}. ${statusLabel}`}
        title={`${title}: ${statusLabel}. ${expanded ? 'Collapse' : 'Expand'} section`}
      >
        <span className="clinical-note-accordion__title-wrap">
          <span
            className={`clinical-note-accordion__status-dot clinical-note-accordion__status-dot--${level}`}
            aria-hidden="true"
          />
          <span className="clinical-note-accordion__title">{title}</span>
          <span
            className={`clinical-note-accordion__status clinical-note-accordion__status--${level}`}
            title={statusLabel}
          >
            {statusLabel}
          </span>
        </span>
        <span className="clinical-note-accordion__chevron" aria-hidden="true">
          {expanded ? '−' : '+'}
        </span>
      </button>

      <div
        id={contentId}
        className="clinical-note-accordion__content"
        style={{ display: collapsed ? 'none' : 'block' }}
        aria-hidden={collapsed ? 'true' : undefined}
        role="region"
        aria-label={`${title} documentation`}
      >
        <div className="clinical-note-accordion__body">{children}</div>

        {(previousSectionKey || nextSectionKey) && (
          <nav className="clinical-note-accordion__navigation" aria-label={`${title} section navigation`}>
            <button
              type="button"
              className="clinical-note-accordion__nav-button"
              onClick={() => goToSection(previousSectionKey)}
              disabled={!previousSectionKey}
            >
              ← Previous
            </button>
            <button
              type="button"
              className="clinical-note-accordion__nav-button clinical-note-accordion__nav-button--primary"
              onClick={() => goToSection(nextSectionKey)}
              disabled={!nextSectionKey}
            >
              Next →
            </button>
          </nav>
        )}
      </div>
    </section>
  );
}
